import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { WindowService } from '@core/window-actions/window.service';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  dotBackgroundOverlayComponentInitialState as initialState,
  DotBackgroundOverlayComponentState,
  DotBackgroundOverlayComponentViewModel,
} from './dot-background-overlay.component.state';

/**
 * @description Display an overlay on top of a background
 * @WhereIsItUsed
 * - booker air form
 * - section header container
 */
@Component({
  selector: 'dot-background-overlay',
  templateUrl: './dot-background-overlay.component.html',
  styleUrls: ['./dot-background-overlay.component.scss'],
  providers: [ComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DotBackgroundOverlayComponent {
  @Input() set opacityType(opacityType: 'solid' | 'radial') {
    this.store.patchState({ opacityType });
  }

  @Input() set customClasses(
    customClasses: string | string[] | Set<string> | { [k: string]: any },
  ) {
    this.store.patchState({ customClasses });
  }

  @Input()
  set bgHexColor(hexColor: string) {
    const longHandHex = hexColor.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => `#${r}${r}${g}${g}${b}${b}`,
    );
    this.store.patchState({ hexColor: longHandHex });
  }

  @Input()
  set opacity(opacity: number) {
    this.store.patchState({
      hexOpacity:
        opacity > 1 || opacity < 0
          ? ''
          : Math.round(opacity * 255).toString(16),
    });
  }

  bgStyles$: Observable<{ [k: string]: string }>;
  vm$: Observable<DotBackgroundOverlayComponentViewModel>;

  constructor(
    public store: ComponentStore<DotBackgroundOverlayComponentState>,
    public windowService: WindowService,
  ) {
    this.store.setState(initialState);

    this.store.patchState(
      this.windowService.isMobile.pipe(
        map(isMobile => ({
          isMobile,
        })),
      ),
    );

    this.bgStyles$ = this.store.select(
      this.store.select(state => state.hexOpacity),
      this.store.select(state => state.hexColor),
      this.store.select(state => state.isMobile),
      this.store.select(state => state.opacityType),
      (hexOpacity, hexColor, isMobile, opacityType) => {
        const color = hexColor ? hexColor + hexOpacity : 'transparent';
        const clip = isMobile ? 'farthest-corner' : 'farthest-side';
        return {
          background:
            opacityType === 'radial'
              ? `radial-gradient(${clip} ellipse at 50% 50%,${color} 50%,transparent)`
              : color,
        };
      },
    );

    this.vm$ = this.store.select(
      this.bgStyles$,
      this.store.select(state => state.customClasses),
      this.store.select(state => state.isMobile),
      this.store.select(state => state.opacityType),
      (
        bgStyles,
        customClasses,
        isMobile,
        opacityType,
      ): DotBackgroundOverlayComponentViewModel => ({
        bgStyles,
        customClasses,
        isMobile,
        isRadial: opacityType === 'radial',
      }),
    );
  }
}
